/**
 * Last-known match view per match, kept on this device so a reload while the game server is down
 * still shows the realm (read-only, with a clear "as of" banner). IndexedDB, because a view plus
 * its terrain map can exceed what localStorage holds comfortably.
 */
import type { GameMap, PlayerView } from '@seven-dawns/shared';

export interface CachedView {
  view: PlayerView;
  map: GameMap | null;
  /** Local time the view was received. */
  savedAtMs: number;
}

export interface ViewCache {
  load(code: string): Promise<CachedView | null>;
  save(code: string, entry: CachedView): Promise<void>;
  remove(code: string): Promise<void>;
}

const DB_NAME = 'seven-dawns-cache';
const STORE = 'views';

function openDb(): Promise<IDBDatabase | null> {
  return new Promise((resolve) => {
    if (typeof indexedDB === 'undefined') return resolve(null);
    try {
      const request = indexedDB.open(DB_NAME, 1);
      request.onupgradeneeded = () => request.result.createObjectStore(STORE);
      request.onsuccess = () => resolve(request.result);
      request.onerror = () => resolve(null);
      request.onblocked = () => resolve(null);
    } catch {
      resolve(null);
    }
  });
}

/** IndexedDB-backed cache. Every failure (private mode, quota) degrades to "no cache". */
export function createIdbViewCache(): ViewCache {
  let db: Promise<IDBDatabase | null> | null = null;
  const getDb = () => (db ??= openDb());
  const run = async <T>(
    mode: IDBTransactionMode,
    op: (store: IDBObjectStore) => IDBRequest,
    fallback: T,
  ): Promise<T> => {
    const handle = await getDb();
    if (!handle) return fallback;
    return new Promise<T>((resolve) => {
      try {
        const request = op(handle.transaction(STORE, mode).objectStore(STORE));
        request.onsuccess = () => resolve((request.result as T | undefined) ?? fallback);
        request.onerror = () => resolve(fallback);
      } catch {
        resolve(fallback);
      }
    });
  };
  const key = (code: string) => code.trim().toUpperCase();
  return {
    load: (code) => run<CachedView | null>('readonly', (s) => s.get(key(code)), null),
    save: async (code, entry) => {
      await run('readwrite', (s) => s.put(entry, key(code)), undefined);
    },
    remove: async (code) => {
      await run('readwrite', (s) => s.delete(key(code)), undefined);
    },
  };
}

/** In-memory cache (tests, demo). */
export function createMemoryViewCache(): ViewCache {
  const entries = new Map<string, CachedView>();
  return {
    load: async (code) => entries.get(code.toUpperCase()) ?? null,
    save: async (code, entry) => {
      entries.set(code.toUpperCase(), entry);
    },
    remove: async (code) => {
      entries.delete(code.toUpperCase());
    },
  };
}
